import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Globe2, MapPin, Building2, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import SectionLabel from './SectionLabel';
import Reveal from './Reveal';
import { CTA_URL } from '../mock';
import { useI18n } from '../i18n/I18nContext';

const COUNTRY_STATIC = [
  { code: 'HU', flag: '🇭🇺', count: '16 000' },
  { code: 'SK', flag: '🇸🇰', count: '13 000' },
  { code: 'CZ', flag: '🇨🇿', count: '3 000' },
  { code: 'AT', flag: '🇦🇹', count: '47 000' },
];

const STEP_ICONS = [Globe2, MapPin, Building2, TrendingUp];

export default function ExpansionBanner() {
  const { t } = useI18n();
  const capitals = t('expansion.capitals') || {};
  const steps = t('expansion.steps') || [];

  return (
    <section className="relative py-24 md:py-32 overflow-hidden">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[520px] w-[820px] rounded-full bg-blue-500/8 blur-[130px]" />
        <div className="absolute bottom-0 right-0 h-[300px] w-[400px] rounded-full bg-sky-500/10 blur-[100px]" />
        <div className="absolute inset-0 grid-lines opacity-30" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-8 space-y-6">
            <SectionLabel>{t('expansion.tag')}</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-slate-900 leading-[1.05]">
              {t('expansion.title_a')} <span className="text-gradient-green">{t('expansion.title_b')}</span>
            </h2>
            <p className="text-slate-700 text-base md:text-lg leading-relaxed max-w-2xl">{t('expansion.desc')}</p>
          </div>
          <div className="lg:col-span-4 lg:pl-6 space-y-4">
            <div className="text-[11px] tracking-[0.22em] uppercase text-blue-600">{t('expansion.markets_label')}</div>
            <div className="flex items-baseline gap-3">
              <div className="font-display text-6xl font-semibold text-gradient-green leading-none">4</div>
              <div className="text-sm text-slate-500 pb-1 whitespace-pre-line">{t('expansion.markets_subtitle')}</div>
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-12">
          {COUNTRY_STATIC.map((c, i) => (
            <Reveal key={c.code} delay={i * 70}>
              <div className="group relative h-full rounded-2xl border border-blue-500/12 bg-white p-6 overflow-hidden hover:border-blue-500/35 card-lift">
                <span className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-blue-500/10 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative flex items-start justify-between mb-8">
                  <span className="text-3xl">{c.flag}</span>
                  <span className="font-mono text-[10px] tracking-widest text-blue-600">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <div className="relative space-y-3">
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-sm tracking-widest text-blue-600">{c.code}</span>
                    <span className="h-1 w-1 rounded-full bg-blue-500/40" />
                    <span className="text-sm text-slate-500">{capitals[c.code]}</span>
                  </div>
                  <div className="font-display text-2xl font-medium text-slate-900">{t(`markets.${c.code}`)}</div>
                  <div className="pt-4 border-t border-blue-500/10">
                    <div className="font-display text-3xl font-semibold text-gradient-green leading-none">{c.count}</div>
                    <div className="mt-1.5 text-[11px] tracking-widest uppercase text-slate-400">{t('expansion.structured_companies')}</div>
                  </div>
                </div>
                <span className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            </Reveal>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-4">
          {steps.map((s, i) => {
            const Icon = STEP_ICONS[i];
            return (
              <Reveal key={s.title} delay={i * 60} className="lg:col-span-3">
                <div className="group relative flex items-start gap-4 rounded-2xl border border-blue-500/10 bg-white px-5 py-5 hover:border-blue-500/30 transition-all h-full">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 border border-blue-500/20 group-hover:bg-blue-500/20 transition-colors">
                    <Icon className="h-4 w-4 text-blue-600" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="font-mono text-[10px] tracking-widest text-blue-600">{String(i + 1).padStart(2, '0')}</span>
                      <div className="font-display text-base font-medium text-slate-900 leading-tight">{s.title}</div>
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-12 relative rounded-3xl border border-blue-500/15 bg-gradient-to-br from-blue-50 via-white to-white p-8 md:p-10 overflow-hidden">
          <div className="absolute -top-24 -left-24 h-64 w-64 rounded-full bg-blue-500/12 blur-[80px]" />
          <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-2">
              <div className="text-[11px] tracking-[0.22em] uppercase text-blue-600">{t('expansion.cta_label')}</div>
              <div className="font-display text-2xl md:text-3xl font-medium text-slate-900 leading-tight max-w-xl">
                {t('expansion.cta_title_a')} <span className="text-gradient-green">{t('expansion.cta_title_b')}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 shrink-0">
              <a href={CTA_URL} target="_blank" rel="noreferrer">
                <Button className="btn-primary bg-blue-500 hover:bg-blue-600 text-white rounded-xl h-11 px-5 font-medium">
                  {t('common.free_consultation')} <ArrowUpRight className="ml-1.5 h-4 w-4" />
                </Button>
              </a>
              <Link to="/services#terjeszkedes">
                <Button variant="outline" className="rounded-xl h-11 px-5 bg-transparent border-blue-500/25 text-slate-700 hover:bg-blue-500/10 hover:text-slate-900 hover:border-blue-500/50">
                  {t('common.learn_more')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
